import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "button-group-container" }
const _hoisted_2 = { class: "include-object-container" }
const _hoisted_3 = {
  class: "include-object-container",
  style: {"margin-top":"-8px"}
}
const _hoisted_4 = { class: "table-container" }
const _hoisted_5 = { key: 2 }
const _hoisted_6 = { class: "table-container" }
const _hoisted_7 = { class: "table__no-items" }

import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import {
  archiveTicket,
  deleteTicket,
  getTicketsPage,
  Page,
  Ticket
} from '@/services/tickets.service'
import ButtonGroupNoIcons from '@/design-system/atoms/buttons/ButtonGroupNoIcons.vue'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import CheckBoxStandard from '@/design-system/atoms/CheckBoxStandard.vue'
import ConfirmDeleteAttachmentCard from '@/components/features/attachments/ConfirmDeleteAttachmentCard.vue'
import InfoLabel from '@/design-system/molecules/InfoLabel.vue'
import OverlayCard from '@/design-system/molecules/OverlayCard.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import TableTicketsMini from '@/components/features/tickets/TableTicketsMini.vue'
import useReportPoi from '@/hooks/reports/useReportPoi'

type Emits = {
  (e: 'toggleFullscreen'): void
  (e: 'closeDashboard'): void
}

export default /*@__PURE__*/_defineComponent({
  __name: 'TicketsDashboard',
  emits: ["toggleFullscreen", "closeDashboard"],
  setup(__props) {


const { t } = useI18n()
const piniaStore = useGeneralStore()
const store = useStore()

let abortController: AbortController | null = null
const buttonText = `${t('general__data__create')} ${t('general__data__new-ticket').toLowerCase()}`
const includeArchivedTickets = ref(false) // Archived and deleted tickets are expected to have the property active: false
const includeChildObject = ref(true)
const includeFinishedTickets = ref(false)
const isLoadingFirstPage = ref(false)
const { isPoiFromBackend } = useReportPoi()
const pageNumber = ref(0)
const showConfirmDelete = ref(false)
const totalNumberOfItems = ref(0)
const totalNumberOfPages = ref(0)
const tabOptions = ref([
  {
    title: t('general__text__priority'),
    value: 'priority',
    active: true,
    disabled: false
  },
  {
    title: t('general__text__status'),
    value: 'status',
    active: false,
    disabled: false
  },
  {
    title: t('general__text__due-date'),
    value: 'dueDate',
    active: false,
    disabled: false
  }
])
const activeTab = computed(() => {
  const activeTab = tabOptions.value.find((option) => option.active)
  return activeTab!.value
})
const clickedItemType = computed(() => {
  return store.state.clickedItemType
})
const currentOverlayModalPinia = computed(() => {
  return piniaStore.currentOverlayModalPinia
})
const isFeatureFullscreen = computed(() => {
  return piniaStore.isFeatureFullscreen
})
const mobileScreen = computed(() => {
  return store.state.mobileScreen
})
const selectedObject = computed(() => {
  return store.getters.selectedObject
})
const selectedTicket = computed(() => piniaStore.selectedTicket)
const showOverlayModalPinia = computed(() => {
  return piniaStore.showOverlayModalPinia
})
const sortOrder = computed(() => {
  return activeTab.value === 'priority' ? 'desc' : 'asc'
})
const ticketsList = computed(() => {
  return piniaStore.ticketsList
})
const fields = computed(() => {
  const fields = [
    {
      key: 'name',
      name: ticketsList.value
        ? totalNumberOfItems.value + ' ' + t('general__label__tickets').toLocaleLowerCase()
        : ''
    },
    {
      key: 'sorting',
      name: getActiveTabName()
    }
  ]
  return fields
})
function changeTab(value) {
  tabOptions.value = tabOptions.value.map((el) => {
    el.active = el.value === value
    return el
  })
}
async function archiveSelectedTicket() {
  if (selectedTicket.value)
    try {
      const ticket = await archiveTicket(selectedTicket.value?.id)
      if (ticket) {
        setInfoMessage('success', t('general__data__ticket-archived'))
        // remove archived ticket from store
        const ticketsArray = piniaStore.ticketsList.filter((t) => t.id !== ticket.id)
        piniaStore.setTicketsList([...ticketsArray])
        piniaStore.setSelectedTicket(null)
        totalNumberOfItems.value--
      }
    } catch (error) {
      setInfoMessage('error', 'general__data__ticket-archive-failed')
    }
}
function createTicket() {
  piniaStore.setSelectedTicket(null)
  piniaStore.setCurrentOverlayModalPinia('createTicket')
  piniaStore.setShowOverlayModalPinia(true)
}
async function deleteSelectedTicket() {
  if (selectedTicket.value) {
    const ticketId = selectedTicket.value.id
    try {
      const res = await deleteTicket(ticketId)
      if (res >= 200 && res < 300) {
        setInfoMessage('success', t('general__data__ticket-deleted'))
        // remove archived ticket from store
        const ticketsArray = piniaStore.ticketsList.filter((t) => t.id !== ticketId)
        piniaStore.setTicketsList([...ticketsArray])
        piniaStore.setSelectedTicket(null)
        totalNumberOfItems.value--
      }
    } catch (error) {
      setInfoMessage('error', t('general__data__ticket-delete-failed'))
    }
  }
}
async function loadMoreTickets() {
  if (pageNumber.value === totalNumberOfPages.value) {
    return
  }
  if (!store.getters.selectedObjectId) return
  pageNumber.value++
  const ticketsPage: Page<Ticket> | undefined | 'canceled' = await getTicketsPage(
    includeChildObject.value,
    includeFinishedTickets.value,
    includeArchivedTickets.value,
    pageNumber.value,
    25,
    {
      entityId: store.getters.selectedObjectId
    },
    `${activeTab.value},${sortOrder.value}`
  )
  if (ticketsPage !== 'canceled' && ticketsPage?.content?.length) {
    piniaStore.setTicketsList([...ticketsList.value, ...ticketsPage.content])
  }
}
function getActiveTabName() {
  if (activeTab.value === 'status') return t('general__text__status')
  if (activeTab.value === 'priority') return t('general__text__priority')
  if (activeTab.value === 'dueDate') return t('general__text__due-date')
  return ''
}
async function refreshList() {
  if (abortController) abortController.abort()
  if (!store.getters.selectedObjectId) return
  abortController = new AbortController()
  piniaStore.setSelectedTicket(null)
  piniaStore.setTicketsList([])
  pageNumber.value = 0
  totalNumberOfPages.value = 0
  totalNumberOfItems.value = 0
  isLoadingFirstPage.value = true
  const ticketsPage: Page<Ticket> | undefined | 'canceled' = await getTicketsPage(
    includeChildObject.value,
    includeFinishedTickets.value,
    includeArchivedTickets.value,
    pageNumber.value,
    25,
    {
      entityId: store.getters.selectedObjectId
    },
    `${activeTab.value},${sortOrder.value}`,
    abortController.signal
  )
  if (ticketsPage !== 'canceled' && ticketsPage?.content?.length) {
    totalNumberOfPages.value = ticketsPage.totalPages
    totalNumberOfItems.value = ticketsPage.totalElements
    piniaStore.setTicketsList(ticketsPage.content)
  }
  if (ticketsPage !== 'canceled') isLoadingFirstPage.value = false
}
function setInfoMessage(type: string, message: string) {
  store.commit('setInfoMessage', { message, type })
}

watch(activeTab, () => {
  refreshList()
})
watch(includeChildObject, () => {
  refreshList()
})
watch(includeFinishedTickets, () => {
  refreshList()
})
watch(
  selectedObject,
  async (newSelection, oldSelection) => {
    if (newSelection === 0) {
      // Don't include child objects or finished tickets for realm, if activated for realm in the future
      includeChildObject.value = false
      includeFinishedTickets.value = false
    }
    if (newSelection !== 0 && oldSelection === 0) {
      // If realm was previously selected, include child objects for the new selection
      includeChildObject.value = true
    }
    if (newSelection !== 0) {
      refreshList()
    }
  },
  { immediate: true }
)
watch(showOverlayModalPinia, () => {
  if (currentOverlayModalPinia.value === 'createTicket' && !showOverlayModalPinia.value) {
    refreshList()
  }
})
onBeforeUnmount(() => {
  if (abortController) abortController.abort()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (showConfirmDelete.value)
      ? (_openBlock(), _createBlock(OverlayCard, {
          key: 0,
          class: _normalizeClass(["overlay-confirm-delete", {
      'overlay-confirm-delete__mobile': mobileScreen.value,
      'overlay-confirm-delete__fullscreen': isFeatureFullscreen.value && !mobileScreen.value
    }]),
          onCloseOverlay: _cache[1] || (_cache[1] = ($event: any) => (showConfirmDelete.value = false))
        }, {
          default: _withCtx(() => [
            _createVNode(ConfirmDeleteAttachmentCard, {
              buttonText: _unref(t)('general__text__delete') + ' ' + _unref(t)('general__data__ticket').toLowerCase(),
              onCloseOverlay: _cache[0] || (_cache[0] = ($event: any) => (showConfirmDelete.value = false)),
              onConfirmDelete: deleteSelectedTicket
            }, null, 8, ["buttonText"])
          ]),
          _: 1
        }, 8, ["class"]))
      : _createCommentVNode("", true),
    (
      clickedItemType.value === 'realEstate' ||
      clickedItemType.value === 'building' ||
      clickedItemType.value === 'level' ||
      clickedItemType.value === 'room' ||
      clickedItemType.value == 'zone' ||
      clickedItemType.value == 'line' ||
      clickedItemType.value == 'poi'
    )
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          id: "tickets-dashboard",
          class: _normalizeClass({ fullscreen: isFeatureFullscreen.value && !mobileScreen.value, mobile: mobileScreen.value })
        }, [
          _createElementVNode("div", _hoisted_1, [
            _createVNode(ButtonGroupNoIcons, {
              tabOptions: tabOptions.value,
              onChangeTab: changeTab
            }, null, 8, ["tabOptions"])
          ]),
          _createElementVNode("div", _hoisted_2, [
            _createVNode(CheckBoxStandard, {
              class: "include-object-container__checkbox",
              checked: includeFinishedTickets.value,
              disabled: selectedObject.value === 0,
              title: _unref(t)('general__data__tickets-include-finished'),
              onValueChanged: _cache[2] || (_cache[2] = ($event: any) => (includeFinishedTickets.value = !includeFinishedTickets.value))
            }, null, 8, ["checked", "disabled", "title"])
          ]),
          _createElementVNode("div", _hoisted_3, [
            _createVNode(CheckBoxStandard, {
              class: "include-object-container__checkbox",
              checked: includeChildObject.value,
              disabled: selectedObject.value === 0,
              title: _unref(t)('general__text__objects-include-child'),
              onValueChanged: _cache[3] || (_cache[3] = ($event: any) => (includeChildObject.value = !includeChildObject.value))
            }, null, 8, ["checked", "disabled", "title"]),
            _createVNode(InfoLabel, {
              class: "include-object-container__info",
              info: _unref(t)('tooltip__tickets-include-child-objects')
            }, null, 8, ["info"])
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(PulseLoader, {
              loading: isLoadingFirstPage.value,
              class: _normalizeClass(["spinner", {
          spinner__fullscreen: isFeatureFullscreen.value && !mobileScreen.value,
          spinner__mobile: mobileScreen.value
        }]),
              color: "#1d234f"
            }, null, 8, ["loading", "class"]),
            (!isLoadingFirstPage.value)
              ? (_openBlock(), _createBlock(TableTicketsMini, {
                  key: 0,
                  fields: fields.value,
                  hasArchiveButton: _unref(store).getters.hasLinkEditorAccess,
                  hasDeleteButton: _unref(store).getters.hasLinkAdminAccess,
                  items: _unref(piniaStore).ticketsList,
                  tabOption: activeTab.value,
                  onArchiveTicket: archiveSelectedTicket,
                  onDeleteTicket: _cache[4] || (_cache[4] = ($event: any) => (showConfirmDelete.value = true)),
                  onLoadMoreTickets: loadMoreTickets
                }, null, 8, ["fields", "hasArchiveButton", "hasDeleteButton", "items", "tabOption"]))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", {
            class: _normalizeClass(["bottom-container", {
        'bottom-container__no-button':
          !_unref(store).getters.hasErrandCreatorAccess ||
          (_unref(store).getters.hasErrandCreatorAccess &&
            clickedItemType.value !== 'realEstate' &&
            clickedItemType.value !== 'building' &&
            clickedItemType.value !== 'level' &&
            clickedItemType.value !== 'room' &&
            clickedItemType.value != 'zone' &&
            clickedItemType.value != 'line' &&
            clickedItemType.value != 'poi')
      }])
          }, [
            (
          _unref(store).getters.hasErrandCreatorAccess &&
          (clickedItemType.value === 'realEstate' ||
            clickedItemType.value === 'building' ||
            clickedItemType.value === 'level' ||
            clickedItemType.value === 'room' ||
            clickedItemType.value == 'zone' ||
            clickedItemType.value == 'line' ||
            (clickedItemType.value == 'poi' && _unref(isPoiFromBackend)))
        )
              ? (_openBlock(), _createBlock(ButtonOutline, {
                  key: 0,
                  buttonText: buttonText,
                  iconLeft: require('@/design-system/assets/icons/Icon-Plus-Small.svg'),
                  size: 'small',
                  style: {"width":"100%"},
                  onOnClicked: createTicket
                }, null, 8, ["iconLeft"]))
              : _createCommentVNode("", true)
          ], 2)
        ], 2))
      : (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, _toDisplayString(_unref(t)('general__text__not-available-for-this-type-object')), 1)
          ])
        ]))
  ], 64))
}
}

})